import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Grid, Button, TextField, MenuItem, Switch } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import TopBar from '../components/topbar';
import SidebarNav from '../components/Sidebar';
import RanchMap from '../components/RanchMap';
import ImageUploader from '../components/ImageUploader';
import { dataGridSx } from '../utils/dataGridTheme';
import '../App.css';

const panelStyle = {
    background: 'rgba(255, 255, 255, 0.23)',
    border: '2px solid rgba(120, 53, 15, 0.6)',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
};

const textFieldSx = {
    '& .MuiInputLabel-root': { color: 'rgba(254, 243, 199, 0.85)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f59e0b' },
    '& .MuiOutlinedInput-root': {
        color: '#ffffff',
        backgroundColor: 'rgba(20, 15, 10, 0.75)',
        borderRadius: '8px',
        '& fieldset': { borderColor: 'rgba(245, 158, 11, 0.5)' },
        '&:hover fieldset': { borderColor: 'rgba(245, 158, 11, 0.8)' },
        '&.Mui-focused fieldset': { borderColor: '#f59e0b' },
    },
    '& .MuiSelect-icon': { color: '#ffffff' },
};

const emptyForm = {
    id: null,
    title: '',
    property_number: '',
    property_type: 'property',
    location: '',
    region: '',
    description: '',
    current_bid: 0,
    tags: '',
    images: '',
    contact_info: '',
    map_image_url: '',
    map_x: '',
    map_y: ''
};

const RealtorTools = () => {
    const [userRole, setUserRole] = useState('user');
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sourceRows, setSourceRows] = useState([]);
    const [form, setForm] = useState(emptyForm);

    const fetchUserRole = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/auth/role`, {
                credentials: 'include'
            });
            if (response.ok) {
                const { role } = await response.json();
                setUserRole(role);
            }
        } catch (error) {
            console.error('Error fetching user role:', error);
        }
    };

    const fetchListings = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/realtor/listings`, {
                credentials: 'include'
            });
            const result = await response.json();
            setListings(result.map((listing) => ({ id: listing.id, ...listing })));
        } catch (error) {
            console.error('Error fetching listings:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSourceRows = async (type) => {
        try {
            const endpoint = type === 'business' ? 'businesses' : 'properties';
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/realtor/source/${endpoint}`, {
                credentials: 'include'
            });
            const result = await response.json();
            const sorted = result.sort((a, b) => (a.common_name || a.name || '').localeCompare(b.common_name || b.name || ''));
            setSourceRows(sorted);
        } catch (error) {
            console.error('Error fetching source rows:', error);
        }
    };

    useEffect(() => {
        fetchUserRole();
        fetchListings();
        fetchSourceRows('property');
    }, []);

    const handleTypeChange = (property_type) => {
        setForm({ ...form, property_type });
        fetchSourceRows(property_type);
    };

    const handlePrefill = (sourceId) => {
        const source = sourceRows.find((row) => row.id === Number(sourceId));
        if (!source) return;
        setForm({
            ...form,
            title: source.common_name || source.name || '',
            property_number: String(source.id),
            current_bid: source.price || 0
        });
    };

    const resetForm = () => setForm(emptyForm);

    const handleSubmit = async () => {
        try {
            const body = {
                ...form,
                tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
                images: form.images.split(',').map((i) => i.trim()).filter(Boolean)
            };

            const isEdit = !!form.id;
            const url = isEdit
                ? `${import.meta.env.VITE_REACT_API_URL}/api/realtor/listings/${form.id}`
                : `${import.meta.env.VITE_REACT_API_URL}/api/realtor/listings`;

            const response = await fetch(url, {
                method: isEdit ? 'PATCH' : 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                throw new Error('Failed to save listing');
            }
            resetForm();
            fetchListings();
        } catch (error) {
            console.error('Error saving listing:', error);
        }
    };

    const handleEdit = (row) => {
        setForm({
            id: row.id,
            title: row.title || '',
            property_number: row.property_number || '',
            property_type: row.property_type || 'property',
            location: row.location || '',
            region: row.region || '',
            description: row.description || '',
            current_bid: row.current_bid || 0,
            tags: JSON.parse(row.tags || '[]').join(', '),
            images: JSON.parse(row.images || '[]').join(', '),
            contact_info: row.contact_info || '',
            map_image_url: row.map_image_url || '',
            map_x: row.map_x !== null && row.map_x !== undefined ? row.map_x : '',
            map_y: row.map_y !== null && row.map_y !== undefined ? row.map_y : ''
        });
    };

    const handleToggleAvailable = async (row) => {
        try {
            await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/realtor/listings/${row.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_available: !row.is_available })
            });
            fetchListings();
        } catch (error) {
            console.error('Error toggling availability:', error);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/realtor/listings/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to delete listing');
            }
            fetchListings();
        } catch (error) {
            console.error('Error deleting listing:', error);
        }
    };

    const columns = [
        { field: 'title', headerName: 'Title', width: 220 },
        { field: 'property_type', headerName: 'Type', width: 100 },
        { field: 'current_bid', headerName: 'Current Bid', width: 120 },
        {
            field: 'is_available',
            headerName: 'Available',
            width: 110,
            renderCell: (params) => (
                <Switch checked={!!params.row.is_available} onChange={() => handleToggleAvailable(params.row)} />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            renderCell: (params) => (
                <Box>
                    <Button variant="contained" size="small" onClick={() => handleEdit(params.row)} sx={{ mr: 1 }}>
                        Edit
                    </Button>
                    {userRole === 'admin' && (
                        <Button variant="contained" color="error" size="small" onClick={() => handleDelete(params.row.id)}>
                            Delete
                        </Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <div className="businessgrid">
            <div className="topNav">
                <TopBar />
            </div>
            <div className="sidebar-container">
                <SidebarNav />
            </div>
            <div className='test'>
                <h1 style={{ textAlign: 'center', color: 'white', fontFamily: 'Playfair Display, serif', fontSize: '50px', marginBottom: '0px' }}>Realtor Tools</h1>
                <p style={{ textAlign: 'center', color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '20px', marginTop: '0px' }}>Manage Listings</p>

                <div style={{ padding: '20px' }}>
                    <Card sx={{ ...panelStyle, mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff', fontFamily: 'serif' }}>
                                {form.id ? 'Edit Listing' : 'Create Listing'}
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField select label="Type" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.property_type}
                                        onChange={(e) => handleTypeChange(e.target.value)}>
                                        <MenuItem value="property">Property</MenuItem>
                                        <MenuItem value="business">Business</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField select label="Prefill from available listing" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} defaultValue=""
                                        onChange={(e) => handlePrefill(e.target.value)}>
                                        {sourceRows.map((row) => (
                                            <MenuItem key={row.id} value={row.id}>
                                                {row.common_name || row.name} (${row.price})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Title" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Property/Business Number" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.property_number} onChange={(e) => setForm({ ...form, property_number: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Location" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField select label="Region" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
                                        <MenuItem value="Ambarino">Ambarino</MenuItem>
                                        <MenuItem value="New Hanover">New Hanover</MenuItem>
                                        <MenuItem value="Lemoyne">Lemoyne</MenuItem>
                                        <MenuItem value="West Elizabeth">West Elizabeth</MenuItem>
                                        <MenuItem value="New Austin">New Austin</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField label="Description" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Current Bid" type="number" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.current_bid} onChange={(e) => setForm({ ...form, current_bid: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Contact Information" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.contact_info} onChange={(e) => setForm({ ...form, contact_info: e.target.value })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField label="Tags (comma separated)" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField label="Image URLs (comma separated)" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} />
                                    <Box sx={{ mt: 1 }}>
                                        <ImageUploader onUploaded={(url) => setForm((prev) => ({ ...prev, images: prev.images ? `${prev.images}, ${url}` : url }))} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField label="Map Image URL (optional custom shot)" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.map_image_url} onChange={(e) => setForm({ ...form, map_image_url: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Game X" type="number" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.map_x} onChange={(e) => setForm({ ...form, map_x: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Game Y" type="number" fullWidth sx={textFieldSx} InputLabelProps={{ shrink: true }} value={form.map_y} onChange={(e) => setForm({ ...form, map_y: e.target.value })} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ color: 'rgba(254, 243, 199, 0.7)', mb: 1 }}>
                                        Enter coordinates above, or click the map to place the listing pin{form.map_x !== '' && form.map_y !== '' ? ` (${Number(form.map_x).toFixed(1)}, ${Number(form.map_y).toFixed(1)})` : ''}
                                    </Typography>
                                    <RanchMap
                                        position={form.map_x !== '' && form.map_y !== '' ? [Number(form.map_x), Number(form.map_y)] : null}
                                        onPick={([x, y]) => setForm({ ...form, map_x: x, map_y: y })}
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                <Button variant="contained" onClick={handleSubmit}>{form.id ? 'Save Changes' : 'Create Listing'}</Button>
                                {form.id && <Button onClick={resetForm}>Cancel</Button>}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={panelStyle}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff', fontFamily: 'serif' }}>
                                Listings
                            </Typography>
                            <div style={{ height: 400, width: '100%' }}>
                                <DataGrid
                                    rows={listings}
                                    columns={columns}
                                    pageSize={5}
                                    rowsPerPageOptions={[5]}
                                    loading={loading}
                                    sx={dataGridSx}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default RealtorTools;
